// 📚 useHouseLibrary: the House library's whole client state, in ONE hook
// (HOUSE_LIBRARY_SPEC_2026_09_30.md §3 + §4.3 W2; the backend =
// justsomeguy-backend/src/ChatGame/houseBooks.js, its payload notes =
// ~/Desktop/spaces-redesign-2026-09-29/library-build/W1_NOTES.md).
//
// GameStepPage calls this once and keeps its own hunks small. Everything here
// keys on `house.book` being PRESENT in the status payload: absent (the
// backend's HOUSE_LIBRARY_ENABLED off, or not the house) = `live: false` and
// every surface renders nothing, so the page is today's page.
//
//   · ONE held book state ({ book, at }), fed by the status poll, the
//     `house_book_state` socket message and bookControl answers, through ONE
//     rule (utils/houseLibrary.pickNewerBook): a stale poll or an out-of-order
//     message never rewinds a page.
//   · Every write goes through bookControl (POST /api). NEVER the interjection
//     path: that one wakes the house (a round = 7-12 paid AI turns).
//   · /borrow outside the library walks you there first (the /room mover),
//     then opens the shelf or the book once the camera has landed.
//   · The desktop reader sits BESIDE the chat (Nina 09-30, desktop B): while it
//     shows, the ghost column folds so the middle column can split (the GHOST
//     edge tab brings it back, and then a narrow middle stacks the book above
//     the chat instead). Phones read full screen (phone B) and fold to a bar.
// Player copy: short, punchy, no em dashes.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LIBRARY_ROOM, pickNewerBook, isBookOpen, openKey, shelfChipLabel, isShelfChip,
  parseBorrow, oddBookFor, loadShelf, postBookControl, joinLineStep, joinLineText,
  readJoinStore, writeJoinStore, sameBookView,
} from '../utils/houseLibrary';

/** The width the middle column needs before the book can sit beside the chat. */
export const SPLIT_MIN_W = 680;
const PENDING_MS = 30 * 1000; // a walk-then-open that never lands is dropped
const EDGE_TABS_W = 52;        // the right-edge tab stack's width + a little air
const SKEW_SLACK_MS = 2000;    // a server-clock offset that moved more than this is re-taken
const ONE_SEC = 'One sec.';    // a floored open/close (the backend's per-room floor)

const isPayload = (b) => !!b && typeof b === 'object' && !Array.isArray(b) && typeof b.state === 'string';
/** The server clock minus the local one when a payload arrived (null = no `now`). */
const skewOf = (b, at) => (b && Number.isFinite(Number(b.now)) && Number.isFinite(Number(at)) ? Number(b.now) - Number(at) : null);

/** Fold a payload into the held state { book, at, skew }: a newer (or equal) seq
 *  replaces it and stamps when it arrived; an older one is ignored.
 *  ⭐ `force` (ONLY a stale answer to this page's own bookControl request, never
 *  a poll or the socket) takes the payload whatever its seq: the server's
 *  house_book.seq can be rewound under what this page holds, and the seq rule
 *  alone would then drop every answer and freeze the holder.
 *  ⭐ The same state again (a poll where only `now` moved) keeps the held object,
 *  so a status poll never re-renders the library. The clock offset (`skew`) is
 *  kept with it, so the 60 s "Take" timer keeps running off the first arrival;
 *  an offset that jumped (a laptop waking, a changed clock) is taken fresh. */
export function ingestBook(prev, incoming, now = Date.now(), { force = false } = {}) {
  const held = prev && prev.book ? prev.book : null;
  const next = force ? (isPayload(incoming) ? incoming : held) : pickNewerBook(held, incoming);
  if (next === held) return prev || null;
  const skew = skewOf(next, now);
  if (held && sameBookView(held, next)) {
    const was = prev && Number.isFinite(prev.skew) ? prev.skew : skewOf(held, prev && prev.at);
    if (skew == null || was == null || Math.abs(skew - was) <= SKEW_SLACK_MS) return prev;
  }
  return { book: next, at: now, skew };
}

export default function useHouseLibrary({
  gameId, humanPlayerId, houseStatus, viewerRoom, roomCameraOn, isMobile, isBotMode,
  goRoom, setRoomNotice, roomCurtain, placesNav, mobileView, centerColumnRef, inputFormRef,
}) {
  const payload = houseStatus ? houseStatus.book : undefined;
  const live = !!(payload && typeof payload === 'object');
  const [held, setHeld] = useState(null);
  // the status poll seeds it (and the key's absence switches everything off)
  useEffect(() => {
    if (!payload || typeof payload !== 'object') { setHeld(null); return; }
    setHeld((prev) => ingestBook(prev, payload));
  }, [payload]);
  const liveRef = useRef(live);
  liveRef.current = live;
  const takeBook = useCallback((b, force) => {
    if (!liveRef.current || !b || typeof b !== 'object') return;
    setHeld((prev) => ingestBook(prev, b, Date.now(), { force }));
  }, []);
  // the socket's `house_book_state` (stable: the page's socket closure is old). Never forced.
  const ingest = useCallback((b) => takeBook(b, false), [takeBook]);
  // the socket's `house_book_ask`: the ping rides the state that carries it
  const ingestAsk = useCallback((msg) => {
    if (!liveRef.current || !msg || !msg.ask) return;
    setHeld((prev) => {
      if (!prev || !prev.book || Number(prev.book.seq) !== Number(msg.seq)) return prev;
      return { ...prev, book: { ...prev.book, ask: msg.ask } };
    });
  }, []);
  // once the library has been live on this page, the feed keeps its wrapper
  // (a flag that flickers must never remount the chat twice)
  const wrapLatchRef = useRef(false);
  if (live) wrapLatchRef.current = true;

  const book = live && held ? held.book : null;
  const heldAt = held ? held.at : null;
  const open = isBookOpen(book);
  const key = openKey(book);
  const here = live && open && roomCameraOn && viewerRoom === LIBRARY_ROOM && !!humanPlayerId;
  const deskReading = here && !isMobile && !isBotMode;
  const phoneShown = here && isMobile && !isBotMode && (!placesNav || mobileView === 'chat');

  // ── the shelf manifest (shelf.json, fetched once, cached in the module) ──
  const base = book && book.base_url ? book.base_url : (payload && payload.base_url) || null;
  const [manifest, setManifest] = useState(null);
  const [shelfLoad, setShelfLoad] = useState('idle');
  const [shelfNonce, setShelfNonce] = useState(0);
  const [shelfOpen, setShelfOpen] = useState(false);
  const wantShelf = live && !!base && (here || shelfOpen);
  useEffect(() => {
    if (!wantShelf || manifest) return undefined;
    let alive = true;
    setShelfLoad('loading');
    loadShelf(base)
      .then((m) => { if (alive) { setManifest(m); setShelfLoad(m ? 'ready' : 'error'); } })
      .catch(() => { if (alive) setShelfLoad('error'); });
    return () => { alive = false; };
  }, [wantShelf, base, manifest, shelfNonce]);
  const retryShelf = useCallback(() => setShelfNonce((n) => n + 1), []);

  // ── writes ──
  const act = useCallback(async (action, params = {}) => {
    const res = await postBookControl({ gameId, playerId: humanPlayerId, action, params });
    // a STALE answer to our own request is the server's truth, even below the seq we hold
    if (res && res.book && typeof res.book === 'object') takeBook(res.book, res.stale === true);
    return res;
  }, [gameId, humanPlayerId, takeBook]);

  // ── the phone fold (keyed to ONE open: a new book opens full screen) ──
  const [fold, setFold] = useState(null); // { key, folded }
  const phoneFolded = !!(fold && fold.key === key && fold.folded);
  const foldPhone = useCallback(() => setFold({ key, folded: true }), [key]);
  const unfoldPhone = useCallback(() => setFold({ key, folded: false }), [key]);

  // ── the ghost column (desktop B): folded while reading, unless you bring it back ──
  const [ghostKeptKey, setGhostKeptKey] = useState(null);
  const ghostFold = deskReading && ghostKeptKey !== key;
  const keepGhost = useCallback(() => setGhostKeptKey(key), [key]);

  // ── the middle column's width decides split vs stack ──
  const [colW, setColW] = useState(null);
  const [colGap, setColGap] = useState(null); // px between the middle column and the window's right edge
  // the chat's own top-right corner inside the book row: the page's floating 📸 button rides
  // the chat, never the book (it sits at the column's top-right, which is the book's head now)
  const [fabShift, setFabShift] = useState(null); // { top, right } | null
  useEffect(() => {
    if (!deskReading) { setFabShift(null); return undefined; }
    const el = centerColumnRef && centerColumnRef.current;
    if (!el) return undefined;
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const w = Math.round(r.width);
        const gap = Math.round(window.innerWidth - r.right);
        setColW((p) => (p === w ? p : w));
        setColGap((p) => (p === gap ? p : gap));
        const row = el.querySelector('[data-book-row]');
        const feed = row ? row.querySelector('[data-chat-scroll="main"]') : null;
        if (row && feed) {
          const rr = row.getBoundingClientRect();
          const fr = feed.getBoundingClientRect();
          const next = { top: Math.max(0, Math.round(fr.top - rr.top)), right: Math.max(0, Math.round(rr.right - fr.right)) };
          setFabShift((p) => (p && p.top === next.top && p.right === next.right ? p : next));
        }
      });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) {
      ro.observe(el);
      const row = el.querySelector('[data-book-row]');
      if (row) ro.observe(row);
    }
    window.addEventListener('resize', measure);
    const id = setInterval(measure, 1500); // the book row settles after the ghost column folds
    return () => { cancelAnimationFrame(raf); clearInterval(id); if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [deskReading, centerColumnRef]);
  const deskMode = !deskReading ? null : (colW == null || colW >= SPLIT_MIN_W ? 'split' : 'stack');
  // the fixed edge tabs (DOSSIER · SHELF · MEMORY · GHOST) ride the window's right edge: with the
  // ghost column folded the middle column runs under them, so the book keeps clear of that strip
  const deskReserve = deskMode === 'split' && colGap != null ? Math.max(0, EDGE_TABS_W - colGap) : 0;

  // ── the phone dock's top edge (the reader ends there: the box stays reachable) ──
  const [dockTop, setDockTop] = useState(null);
  const phoneReading = phoneShown && !phoneFolded;
  useEffect(() => {
    if (!phoneReading) return undefined;
    let raf = 0;
    const measure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = inputFormRef && inputFormRef.current;
        const vh = window.innerHeight;
        const top = el ? Math.round(el.getBoundingClientRect().top) : null;
        const next = (top != null && top > 80 && top <= vh) ? top : null;
        setDockTop((p) => (p === next ? p : next));
      });
    };
    measure();
    const el = inputFormRef && inputFormRef.current;
    const ro = (el && typeof ResizeObserver !== 'undefined') ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    const vv = window.visualViewport;
    window.addEventListener('resize', measure);
    if (vv) { vv.addEventListener('resize', measure); vv.addEventListener('scroll', measure); }
    const id = setInterval(measure, 1000); // the dock grows and shrinks (palette, polls, the fold)
    return () => {
      cancelAnimationFrame(raf); clearInterval(id);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
      if (vv) { vv.removeEventListener('resize', measure); vv.removeEventListener('scroll', measure); }
    };
  }, [phoneReading, inputFormRef]);

  // ── the shelf sheet ──
  const [shelfNote, setShelfNote] = useState(null);
  const [oddPick, setOddPick] = useState(null);
  const [busySlug, setBusySlug] = useState(null);
  const openShelf = useCallback((opts = {}) => {
    setShelfNote(opts.note || null);
    setOddPick(opts.odd || null);
    setShelfOpen(true);
  }, []);
  const closeShelf = useCallback(() => { setShelfOpen(false); setShelfNote(null); setOddPick(null); }, []);
  useEffect(() => { if (viewerRoom !== LIBRARY_ROOM) setShelfOpen(false); }, [viewerRoom]);
  useEffect(() => { if (!live) setShelfOpen(false); }, [live]);

  // ── open a book (here) or walk there first ──
  const pendingRef = useRef(null); // { parsed, at }
  const runBorrow = useCallback(async (parsed) => {
    if (!parsed || parsed.kind === 'shelf') { openShelf(); return; }
    // the shelf first (one cached fetch): an Odd shelf title opens its silly page,
    // never a server open, even before anyone has opened the shelf
    let m = manifest;
    if (!m && base) {
      m = await loadShelf(base).catch(() => null);
      if (m) { setManifest(m); setShelfLoad('ready'); }
    }
    const odd = oddBookFor(parsed.query, m);
    if (odd) { openShelf({ odd: odd.title }); return; }
    setBusySlug(parsed.query);
    const res = await act('open', { book: parsed.query });
    setBusySlug(null);
    const say = (line) => { if (shelfOpen) setShelfNote(line); else setRoomNotice(line); };
    if (res && res.ok && res.too_fast) { say(ONE_SEC); return; } // the per-room floor: nothing opened
    if (res && res.ok && res.stale) { say('Someone got there first.'); return; } // lost the race: the hook took the real state
    if (res && res.ok) {
      closeShelf();
      setFold({ key: openKey(res.book) || key, folded: false });
      return;
    }
    const line = (res && res.error) || "That one won't open right now.";
    if (res && res.code === 'not_on_shelf') { openShelf({ note: line }); return; }
    say(line);
  }, [act, closeShelf, openShelf, manifest, base, key, shelfOpen, setRoomNotice]);
  const walkThen = useCallback((parsed) => {
    pendingRef.current = { parsed, at: Date.now() };
    if (goRoom) goRoom(LIBRARY_ROOM);
  }, [goRoom]);
  // the walk landed (and the arrival card is done): do what was asked
  useEffect(() => {
    const p = pendingRef.current;
    if (!p) return;
    if (Date.now() - p.at > PENDING_MS) { pendingRef.current = null; return; }
    if (viewerRoom !== LIBRARY_ROOM || !live) return;
    // a book opens at once (the reader fades in with the page); the shelf
    // sheet waits for the arrival card, never a sheet over the postcard
    if (roomCurtain && (!p.parsed || p.parsed.kind === 'shelf')) return;
    pendingRef.current = null;
    runBorrow(p.parsed);
  }, [viewerRoom, roomCurtain, live, runBorrow]);

  /** /borrow [title] (client-side while the library is live). */
  const borrow = useCallback((text) => {
    const parsed = parseBorrow(text);
    if (viewerRoom !== LIBRARY_ROOM) { walkThen(parsed); return; }
    runBorrow(parsed);
  }, [viewerRoom, walkThen, runBorrow]);
  /** A spine on the shelf. */
  const openBook = useCallback((slug) => {
    const parsed = { kind: 'open', query: String(slug || '') };
    if (viewerRoom !== LIBRARY_ROOM) { setShelfOpen(false); walkThen(parsed); return; }
    runBorrow(parsed);
  }, [viewerRoom, walkThen, runBorrow]);
  /** The Reading now row's button: Join (elsewhere) · Read (here). */
  const joinOrRead = useCallback(() => {
    setShelfOpen(false);
    if (viewerRoom !== LIBRARY_ROOM) { if (goRoom) goRoom(LIBRARY_ROOM); return; }
    setFold({ key, folded: false });
  }, [viewerRoom, goRoom, key]);
  /** The 📚 chip: a folded phone book opens; anything else opens the shelf. */
  const onChip = useCallback(() => {
    if (isMobile && open && phoneFolded && viewerRoom === LIBRARY_ROOM) { unfoldPhone(); return; }
    openShelf();
  }, [isMobile, open, phoneFolded, viewerRoom, unfoldPhone, openShelf]);

  // ── the join line (visitors in another room; once per open per device) ──
  const [joinStore, setJoinStore] = useState(() => (gameId ? readJoinStore(gameId) : null));
  // (only once a state is HELD: before the first payload lands, "no book" is not a close)
  const heldNow = live && !!held;
  // (the clock is read when the book or the room changes: a new open inside the 10-min
  // quiet time waits, and shows on a later change once the quiet time is over)
  const step = useMemo(() => (heldNow ? joinLineStep(joinStore, book, viewerRoom, Date.now()) : { show: false, store: joinStore }), [heldNow, joinStore, book, viewerRoom]);
  useEffect(() => {
    if (!heldNow) return; // the key's absence (or a payload not in yet) never touches the device's memory
    const a = JSON.stringify(step.store || null);
    const b = JSON.stringify(joinStore || null);
    if (a === b) return;
    setJoinStore(step.store);
    if (gameId) writeJoinStore(gameId, step.store);
  }, [step, joinStore, gameId, heldNow]);
  const dismissJoin = useCallback(() => {
    if (!key) return;
    const next = { ...(joinStore && typeof joinStore === 'object' ? joinStore : {}), key, seen: true }; // keeps shown_at (the quiet time)
    setJoinStore(next);
    if (gameId) writeJoinStore(gameId, next);
  }, [key, gameId, joinStore]);
  const joinShown = live && step.show && !!humanPlayerId && !isBotMode && roomCameraOn && viewerRoom !== LIBRARY_ROOM;
  const join = joinShown ? {
    text: joinLineText(book),
    onJoin: () => { dismissJoin(); if (goRoom) goRoom(LIBRARY_ROOM); },
    onDismiss: dismissJoin,
  } : null;

  return {
    live, book, heldAt, open, key, here, deskReading, deskMode, deskReserve, fabShift: deskReading ? fabShift : null, phoneShown, phoneReading, phoneFolded, dockTop,
    wrapLatched: wrapLatchRef.current,
    manifest, shelfLoad, retryShelf, shelfOpen, openShelf, closeShelf, shelfNote, oddPick, setOddPick, busySlug,
    chipLabel: shelfChipLabel(book), isChip: (v) => live && isShelfChip(v), onChip,
    act, ingest, ingestAsk, borrow, openBook, joinOrRead, foldPhone, unfoldPhone,
    ghostFold, keepGhost, join,
  };
}
